import type { Asset, MorphPairing, Scene } from '@lumen5/luminary';
import {
    deriveMatchCutPairings,
    distanceBetween,
    getMorphableAssets,
    getTextMorphUnits,
    sharesNativelyMorphingAsset,
} from '@lumen5/luminary';

import {
    getPrePackagedMorphPosition,
    hasAdjacentMorphEnabledScene,
} from 'js/src/luminary/luminary-selectors';

export type MorphTechniqueId = 'text';

export type MorphDisqualifiedReason =
    | 'no-morphable-assets'
    | 'incompatible-structure'
    | 'below-threshold'
    | 'shares-morphable-content'
    | 'consecutive-morph-limit'
    | 'scene-sequence';

export type MorphRecommendation = {
    recommended: boolean;
    score: number;
    techniqueId: MorphTechniqueId | null;
    disqualifiedReason: MorphDisqualifiedReason | null;
    pairings: MorphPairing[];
};

export const DEFAULT_MORPH_SCORE_THRESHOLD = 0.5;

// Below this fraction of the canvas diagonal, a text match-cut reads as a blink in place, not a cut.
const MIN_TEXT_TRAVEL_FRACTION = 0.04;

function referenceDiagonal(scene: Scene): number {
    const { width, height } = scene.root.referenceResolution;
    return Math.hypot(width, height);
}

function travelFraction(source: Asset, target: Asset, diagonal: number): number {
    if (diagonal === 0) return 0;
    return distanceBetween(source, target) / diagonal;
}

// Size/area is deliberately not a scoring factor — a match-cut reads fine between differently-sized text.
function scoreMatchCutPairings(
    sourceScene: Scene,
    targetScene: Scene,
    pairings: MorphPairing[],
): number {
    const sourceTextUnitCount = getTextMorphUnits(sourceScene, 'end').length;
    const targetTextUnitCount = getTextMorphUnits(targetScene, 'start').length;
    const diagonal = referenceDiagonal(sourceScene);

    const totalTravel = pairings.reduce((sum, pairing) => {
        const source = sourceScene.getAssetById(pairing.sourceId, { deep: true });
        const target = targetScene.getAssetById(pairing.targetId, { deep: true });
        if (!source || !target) {
            throw new Error('match-cut pairing references an asset not found in the given scene');
        }
        return sum + travelFraction(source, target, diagonal);
    }, 0);

    const averageTravel = totalTravel / pairings.length;
    const countMatch =
        Math.min(sourceTextUnitCount, targetTextUnitCount) /
        Math.max(sourceTextUnitCount, targetTextUnitCount);
    const travelSufficiency = averageTravel >= MIN_TEXT_TRAVEL_FRACTION ? 1 : 0;

    return countMatch * travelSufficiency;
}

export function scoreScenePairForMorph(
    sourceScene: Scene,
    targetScene: Scene,
    threshold: number = DEFAULT_MORPH_SCORE_THRESHOLD,
): MorphRecommendation {
    const sources = getMorphableAssets(sourceScene, 'end');
    const targets = getMorphableAssets(targetScene, 'start');

    if (sources.length === 0 || targets.length === 0) {
        return {
            recommended: false,
            score: 0,
            techniqueId: null,
            disqualifiedReason: 'no-morphable-assets',
            pairings: [],
        };
    }

    if (sharesNativelyMorphingAsset(sources, targets)) {
        return {
            recommended: false,
            score: 0,
            techniqueId: null,
            disqualifiedReason: 'shares-morphable-content',
            pairings: [],
        };
    }

    // Uses luminary's shared pairing algorithm (also run by rehydration on load) so apply and reload can't disagree.
    const pairings = deriveMatchCutPairings(sourceScene, targetScene);

    if (pairings.length === 0) {
        return {
            recommended: false,
            score: 0,
            techniqueId: null,
            disqualifiedReason: 'incompatible-structure',
            pairings: [],
        };
    }

    const score = scoreMatchCutPairings(sourceScene, targetScene, pairings);

    if (score < threshold) {
        return {
            recommended: false,
            score,
            techniqueId: 'text',
            disqualifiedReason: 'below-threshold',
            pairings,
        };
    }

    // Only text is paired/scored — other morphable assets are frozen-cut by apply, not paired here.
    return {
        recommended: true,
        score,
        techniqueId: 'text',
        disqualifiedReason: null,
        pairings,
    };
}

export type MorphGapState = {
    morphSceneId: string | null;
    isMorphEnabled: boolean;
    isRecommended: boolean;
};

export function getMorphGapState(intoScene: Scene): MorphGapState {
    const morphScene = intoScene.prevScene;
    if (!morphScene) {
        return { morphSceneId: null, isMorphEnabled: false, isRecommended: false };
    }
    if (
        getPrePackagedMorphPosition(morphScene) !== null ||
        getPrePackagedMorphPosition(intoScene) !== null
    ) {
        return { morphSceneId: morphScene.id, isMorphEnabled: false, isRecommended: false };
    }
    if (hasAdjacentMorphEnabledScene(morphScene)) {
        return {
            morphSceneId: morphScene.id,
            isMorphEnabled: morphScene.morphEnabled,
            isRecommended: false,
        };
    }
    const recommendation = scoreScenePairForMorph(morphScene, intoScene);
    return {
        morphSceneId: morphScene.id,
        isMorphEnabled: morphScene.morphEnabled,
        isRecommended: recommendation.recommended,
    };
}
